import React from "react";
import { useTranslation } from "@/lib/i18n";
import { fmtCurrency } from "@/components/portfolio/_format";
import InfoTooltip from "@/components/portfolio/InfoTooltip";

/**
 * PLBreakdownCard — Phase 8.C-2.
 *
 * Compact secondary KPI row dedicated to the realized vs unrealized vs
 * total P/L breakdown. Sits BELOW the existing PortfolioSummaryCards
 * so the primary KPI row stays uncluttered. Each tile carries a
 * bilingual tooltip that disambiguates:
 *
 *   • Unrealized P/L — open positions only.
 *   • Realized P/L — historical SELL P/L; NOT held as cash inside Stockaily.
 *   • Total P/L — Realized + Unrealized.
 *
 * The "no cash balance" caveat is explicitly carried into the Realized
 * tooltip so users never assume sale proceeds remain accessible inside
 * the platform.
 *
 * Data contract (Phase 8.C-1):
 *   GET /api/portfolio → { pl_breakdown: {
 *     unrealized_pl_total, realized_pl_total, total_pl,
 *     closed_positions_count, sell_count, total_sell_proceeds, total_sell_fees
 *   } }
 */
export default function PLBreakdownCard({ breakdown, dominantCurrency = "USD", previewMode = false }) {
  const { t } = useTranslation();
  if (!breakdown) return null;
  const cur = dominantCurrency || "USD";
  const unrealized = Number(breakdown.unrealized_pl_total || 0);
  const realized = Number(breakdown.realized_pl_total || 0);
  const total = Number(breakdown.total_pl || (unrealized + realized));
  return (
    <div
      data-testid="portfolio-pl-breakdown-card"
      className="rounded-2xl border border-white/10 bg-[rgba(18,18,23,0.85)] shadow-[var(--app-shadow-elev-1)] p-4 sm:p-5"
    >
      <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
        {t("portfolio.pl_card.title")}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <PLTile
          testId="pl-tile-unrealized"
          label={t("portfolio.pl_card.unrealized")}
          tooltip={t("portfolio.pl_card.tooltip_unrealized")}
          value={unrealized}
          currency={cur}
          previewMode={previewMode}
        />
        <PLTile
          testId="pl-tile-realized"
          label={t("portfolio.pl_card.realized")}
          tooltip={t("portfolio.pl_card.tooltip_realized")}
          value={realized}
          currency={cur}
          previewMode={previewMode}
        />
        <PLTile
          testId="pl-tile-total"
          label={t("portfolio.pl_card.total")}
          tooltip={t("portfolio.pl_card.tooltip_total")}
          value={total}
          currency={cur}
          emphasized
          previewMode={previewMode}
        />
      </div>
    </div>
  );
}

function PLTile({ label, tooltip, value, currency, testId, emphasized = false, previewMode = false }) {
  const positive = Number(value) >= 0;
  const tone = positive ? "text-[#2BFF8A]" : "text-[#FF3DA6]";
  return (
    <div
      data-testid={testId}
      data-gated={previewMode ? "true" : undefined}
      className={`rounded-xl border ${emphasized ? "border-white/20 bg-white/[0.04]" : "border-white/10 bg-white/[0.02]"} p-3`}
    >
      <div className="flex items-center gap-1.5">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground truncate">{label}</div>
        <InfoTooltip text={tooltip} testId={`${testId}-tooltip`} label={label} />
      </div>
      <div
        className={`mt-1 tabular ${emphasized ? "text-2xl" : "text-xl"} ${tone} ${previewMode ? "[filter:blur(6px)] select-none pointer-events-none" : ""}`}
        data-testid={`${testId}-value`}
        aria-hidden={previewMode ? "true" : undefined}
      >
        {fmtCurrency(value, { currency, signed: true })}
      </div>
    </div>
  );
}
